import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Truck } from "lucide-react";

const BURG = { background: "#8B1E1E" };

export default function DistributionDialog({ open, onClose, onSaved, locations }) {
  const [central, setCentral] = useState(null);
  const [stock, setStock] = useState([]);
  const [productId, setProductId] = useState("");
  const [allocs, setAllocs] = useState([]);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setProductId(""); setAllocs([]); setNote("");
    setLoading(true);
    Promise.all([
      api.get("/procurement/central-warehouse").catch(() => null),
      api.get("/procurement/central-stock").catch(() => ({ items: [] })),
    ]).then(([wh, s]) => { setCentral(wh); setStock(s?.items || []); })
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!productId) { setAllocs([]); return; }
    // Seed one row per branch location so the user only edits qty.
    setAllocs(locations.map((l) => ({ location_id: l.id, quantity: 0 })));
  }, [productId, locations]);

  const item = useMemo(() => stock.find((s) => s.product_id === productId), [stock, productId]);
  const total = allocs.reduce((s, a) => s + (Number(a.quantity) || 0), 0);
  const setAlloc = (idx, q) => setAllocs((rows) => rows.map((r, i) => i === idx ? { ...r, quantity: q } : r));

  const save = async () => {
    const payload = {
      product_id: productId,
      note,
      allocations: allocs.filter((a) => Number(a.quantity) > 0)
                        .map((a) => ({ location_id: a.location_id, quantity: Number(a.quantity) })),
    };
    if (!payload.product_id) { toast.error("Pick a product"); return; }
    if (!payload.allocations.length) { toast.error("Enter at least one branch quantity"); return; }
    if (item && total > item.quantity + 1e-6) { toast.error(`Only ${item.quantity} available in Central Store`); return; }
    setSaving(true);
    try {
      const d = await api.post("/procurement/distributions", payload);
      toast.success(`Distribution ${d.number} sent to ${d.allocations.length} branch${d.allocations.length > 1 ? "es" : ""}`);
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="dist-dialog">
        <DialogHeader>
          <DialogTitle className="font-brand" style={{ color: "#8B1E1E" }}>Portion Central Stock to Branches</DialogTitle>
          <DialogDescription>Central warehouse: {central?.name || "—"} · pick an item and enter how much each branch should receive.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Product (Central Store)</Label>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger data-testid="dist-product"><SelectValue placeholder={loading ? "Loading…" : "Select from Central Inventory"} /></SelectTrigger>
              <SelectContent>
                {stock.map((s) => (
                  <SelectItem key={s.product_id} value={s.product_id}>{s.product_name} — {s.quantity} {s.unit_code || ""}</SelectItem>
                ))}
                {!stock.length && <SelectItem value="__empty__" disabled>No stock in Central Store</SelectItem>}
              </SelectContent>
            </Select>
            {item && <div className="text-xs mt-1" style={{ color: "#765A4A" }}>Available: <b>{item.quantity}</b> {item.unit_code || ""}</div>}
          </div>
          {productId && (
            <div className="rounded-2xl border p-3" style={{ background: "#FFFDF9", borderColor: "#E9DED0" }}>
              <div className="text-[11px] uppercase tracking-widest font-semibold mb-2" style={{ color: "#765A4A" }}>Allocations per Branch</div>
              <div className="space-y-2">
                {allocs.map((a, i) => {
                  const loc = locations.find((l) => l.id === a.location_id);
                  return (
                    <div key={a.location_id} className="grid grid-cols-[1fr_160px] gap-3 items-center">
                      <div className="text-sm font-medium" style={{ color: "#3B2722" }}>{loc?.name || a.location_id}</div>
                      <Input type="number" min="0" value={a.quantity} onChange={(e) => setAlloc(i, e.target.value)}
                             data-testid={`dist-qty-${loc?.name?.toLowerCase().replace(/\s/g, "-")}`} />
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 pt-2 border-t flex justify-between text-sm" style={{ borderColor: "#E9DED0" }}>
                <span style={{ color: "#765A4A" }}>Total to distribute</span>
                <span className="font-semibold" style={{ color: total > (item?.quantity || 0) ? "#A52222" : "#3B2722" }}>{total} {item?.unit_code || ""}</span>
              </div>
            </div>
          )}
          <div>
            <Label>Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Weekly resupply" data-testid="dist-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dist-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !productId || total <= 0} style={BURG} className="text-white" data-testid="dist-save">
            <Truck size={16} className="mr-1" /> {saving ? "Sending…" : "Send to Branches"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
